import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln } from '../catalog/money';
import { DialogLayer } from '../ui/Dialog';
import { waitingForKsef } from './KsefBadge';
import { nipText, type Invoice, type SendPreview } from './types';

/**
 * Owner 2026-11-01: „przed wyjściem z faktury nie wysłanej do klienta zapytaj, czy wysłać. Podobnie z KSeF. Dodatkowo
 * potwierdzaj przed wysłaniem do KSeF”; „jeśli faktura poszła do KSeF, a nie poszła do klienta, to zapytaj, czy wysłać
 * automatycznie, gdy pojawi się informacja z KSeF”.
 */

/** What leaving the card asks about: issuing a draft, sending it to the client, or sending it once KSeF gives the number. */
export type LeaveQuestion = 'issue' | 'send' | 'auto_send';

/**
 * The question before leaving the invoice, or null. Only about what the user may do (`mayIssue`: „Wystawianie i wysyłka
 * do KSeF”; `maySend`: „Wysyłka do klienta” with the PDF's amounts); corrections and test invoices alike.
 * - a draft that can be issued (its quote version still accepted) → `issue`;
 * - a valid invoice with its PDF (KSeF number, or offline) the client did not get — never sent, the e-mail failed, or the
 *   client has an older PDF → `send` (one only in the server's log, no mail server, does not ask again);
 * - waiting for the KSeF number, never sent, nothing planned → `auto_send`.
 * Neither of the last two without a mail server (review 2).
 */
export function leaveQuestion(inv: Invoice, rights: { mayIssue: boolean; maySend: boolean }): LeaveQuestion | null {
  if (inv.state === 'robocza') return rights.mayIssue && !inv.outdated ? 'issue' : null;
  // review 2: without a mail server nothing would reach the client — neither „Wysłać teraz?” nor the automatic sending
  if (inv.state !== 'wystawiona' || inv.ksefState === 'odrzucona' || !rights.maySend || inv.mailConfigured === false) return null;
  const pdfReady = !!inv.pdfFileId || !!inv.ksefNumber || inv.ksefState === 'offline';
  if (pdfReady) {
    const mayGo = inv.canSend || !inv.pdfFileId;
    const notDelivered = inv.sentAt === null || inv.emailStatus === 'failed' || inv.clientPdfOutdated;
    return mayGo && notDelivered ? 'send' : null;
  }
  return waitingForKsef(inv) && inv.sentAt === null && !inv.autoSend ? 'auto_send' : null;
}

/** „Różnica względem kosztorysu vN: … netto” (the card's bar and the confirmation). */
export const differenceText = (inv: Pick<Invoice, 'quoteVersionNumber' | 'differenceGrosze'>) => `Różnica względem kosztorysu v${inv.quoteVersionNumber}: ${formatPln(inv.differenceGrosze ?? 0)} netto`;

/** Review 3: a TEST/ invoice is e-mailed for real — said in the automatic sending and in the send dialog. */
export function TestInvoiceMailNote({ invoice }: { invoice: Pick<Invoice, 'test'> }) {
  if (!invoice.test) return null;
  return (
    <p className="alert" role="note">
      Faktura testowa – e-mail pójdzie naprawdę na podany adres klienta.
    </p>
  );
}

/** What goes to KSeF: a draft issued („Wystaw”), a rejected one with a new number (KSeF's 440), a corrected rejected one sent again. */
export type KsefSending = 'issue' | 'renumber' | 'resubmit';

/**
 * „Wysłać fakturę do KSeF?” — every way of the card to KSeF goes through it (owner 2026-11-01, review 1): the number
 * (the series it comes from, or the number that stays), the buyer (`buyerName`: as corrected in the form), the gross
 * (with the amount fields), the difference to the quote, and whether KSeF is the test one.
 */
export function IssueConfirmDialog({ invoice, mode = 'issue', buyerName, busy, onConfirm, onClose }: { invoice: Invoice; mode?: KsefSending; buyerName?: string; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  const diff = invoice.state === 'robocza' && invoice.differenceGrosze !== undefined && invoice.differenceGrosze !== 0;
  // a draft goes to the server's KSeF; an issued one stays in its own world (test / real)
  const test = invoice.state === 'robocza' ? invoice.ksefTest : invoice.test;
  const series = invoice.issueSeries ?? 'FV';
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="inv-issue-title" data-feature-id="invoices.issue">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="inv-issue-title">Wysłać fakturę do KSeF?</h2>
        <dl className="inv-confirm">
          <div>
            <dt>Numer</dt>
            <dd>
              {mode === 'issue' && `kolejny numer z serii ${series}, data wystawienia: dziś`}
              {mode === 'renumber' && `nowy numer z serii ${series} zamiast ${invoice.number ?? ''} (ten numer jest już w KSeF), data wystawienia: dziś`}
              {mode === 'resubmit' && `Numer zostaje: ${invoice.number ?? ''}`}
            </dd>
          </div>
          <div>
            <dt>Kontrahent</dt>
            <dd>
              {(buyerName ?? invoice.buyer.name) || '—'}
              {buyerName === undefined && invoice.buyer.nip && `, NIP ${nipText(invoice.buyer.nip)}`}
            </dd>
          </div>
          {invoice.grossGrosze !== undefined && (
            <div>
              <dt>Brutto</dt>
              <dd>{formatPln(invoice.grossGrosze)}</dd>
            </div>
          )}
        </dl>
        {diff && (
          <p className="inv-diff" role="note">
            {differenceText(invoice)}
          </p>
        )}
        {mode === 'resubmit' && <p>Do KSeF pójdzie poprawiona faktura (nowy plik XML) z tym samym numerem.</p>}
        <p className={test ? 'alert' : 'muted'}>
          {test
            ? 'Faktura trafi do testowego KSeF – bez mocy prawnej, z numerem TEST/….'
            : 'Faktura trafi do KSeF i dostanie numer. Potem nie da się jej zmienić ani usunąć – tylko korekta.'}
        </p>
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={busy} onClick={onConfirm}>
            {busy ? 'Wysyłanie…' : 'Wyślij do KSeF'}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

const TITLES: Record<LeaveQuestion, string> = {
  issue: 'Faktura nie jest wystawiona',
  send: 'Faktura nie została wysłana do klienta',
  auto_send: 'Faktura czeka na numer KSeF',
};
const TEXTS: Record<LeaveQuestion, string> = {
  issue: 'Faktura nie jest wystawiona. Wystawić ją teraz?',
  send: 'Faktura nie została wysłana do klienta. Wysłać teraz?',
  auto_send: 'Faktura czeka na numer KSeF. Wysłać ją do klienta automatycznie, gdy KSeF nada numer?',
};

/**
 * The question when the card is left (`useLeavePrompt`). „Wystaw…” / „Wyślij…” open the confirmation and the send
 * dialog (the leaving waits); „Nie teraz” / „Nie” leave; Escape stays. Waiting for the number: the address and DW of
 * the planned e-mail (from the send dialog's defaults), its subject and text filled from the template when it goes.
 */
export function LeaveQuestionDialog({ invoice, question, onIssue, onSend, onPlanned, onLeave, onStay }: { invoice: Invoice; question: LeaveQuestion; onIssue: () => void; onSend: () => void; onPlanned: (v: Invoice) => void; onLeave: () => void; onStay: () => void }) {
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel inv-send" role="alertdialog" aria-modal="true" aria-labelledby="inv-leave-title" aria-describedby="inv-leave-text" data-feature-id={question === 'issue' ? 'invoices.issue' : 'invoices.send'}>
        <DialogLayer onClose={onStay} />
        <h2 id="inv-leave-title">{TITLES[question]}</h2>
        <p id="inv-leave-text">{TEXTS[question]}</p>
        {question === 'auto_send' ? (
          <AutoSendForm invoice={invoice} onPlanned={onPlanned} onLeave={onLeave} />
        ) : (
          <div className="actions">
            <button className="btn primary" type="button" autoFocus onClick={question === 'issue' ? onIssue : onSend}>
              {question === 'issue' ? 'Wystaw…' : 'Wyślij…'}
            </button>
            <button className="btn ghost" type="button" onClick={onLeave}>
              Nie teraz
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AutoSendForm({ invoice, onPlanned, onLeave }: { invoice: Invoice; onPlanned: (v: Invoice) => void; onLeave: () => void }) {
  const preview = useQuery({ queryKey: ['invoices', 'send-preview', invoice.id], queryFn: () => api<SendPreview>(`/api/invoices/${invoice.id}/send-preview`), staleTime: Infinity, gcTime: 0 });
  const [typed, setTyped] = useState<{ to?: string; cc?: string }>({});
  const to = typed.to ?? preview.data?.to ?? '';
  const cc = typed.cc ?? '';
  const [local, setLocal] = useState('');
  const plan = useMutation({ mutationFn: () => api<Invoice>(`/api/invoices/${invoice.id}/auto-send`, { method: 'POST', body: { to, cc, version: invoice.version } }), onSuccess: onPlanned });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (to.trim() === '') return setLocal('Wpisz adres e-mail klienta');
    setLocal('');
    plan.mutate();
  }
  const error = plan.error ?? preview.error;
  if (preview.isLoading) {
    return (
      <p className="muted" role="status">
        Wczytywanie…
      </p>
    );
  }
  // no mail server: nothing would reach the client — not offered, and said why
  if (preview.data?.emailMode === 'log') {
    return (
      <>
        <p className="alert" role="alert">
          Automatycznej wysyłki nie da się zaplanować: serwer poczty nie jest ustawiony (SMTP_URL), więc klient nie dostałby e-maila. Po nadaniu numeru KSeF pobierz PDF i wyślij go ze swojej poczty.
        </p>
        <div className="actions">
          <button className="btn primary" type="button" autoFocus onClick={onLeave}>
            Wyjdź
          </button>
        </div>
      </>
    );
  }
  return (
    <form onSubmit={submit}>
      <label className="field">
        <span>Do</span>
        <input type="email" value={to} onChange={(e) => setTyped((t) => ({ ...t, to: e.target.value }))} autoComplete="off" placeholder="adres e-mail klienta" />
      </label>
      <label className="field">
        <span>DW (nieobowiązkowo)</span>
        <input type="email" value={cc} onChange={(e) => setTyped((t) => ({ ...t, cc: e.target.value }))} autoComplete="off" />
      </label>
      <TestInvoiceMailNote invoice={invoice} />
      <p className="hint">Temat i treść z szablonu (Finanse → Ustawienia → E-mail z fakturą), z numerem KSeF wpisanym w chwili wysyłki. Tylko ta faktura, tylko raz.</p>
      {(local || error) && (
        <p className="alert" role="alert">
          {local || (error instanceof ApiError ? (fieldIssues(error)[0]?.message ?? error.message) : 'Nie udało się zaplanować wysyłki')}
        </p>
      )}
      <div className="actions">
        <button className="btn primary" type="submit" autoFocus disabled={plan.isPending}>
          Tak, wyślij po nadaniu numeru
        </button>
        <button className="btn ghost" type="button" disabled={plan.isPending} onClick={onLeave}>
          Nie
        </button>
      </div>
    </form>
  );
}
